/**
 * src/lib/markets-config.ts — declarative per-market configuration
 * (Phase 7.5a).
 *
 * Until now, "Italy" lived in three places: ForecastItaly.tsx (page
 * title, flag, KPI copy), worker.js (FISCAL_YEAR_START/END, the
 * country_of_origin filter), and the seed scripts. Onboarding a
 * second country meant grepping for "Italy" / "italy" / "🇮🇹" / EUR
 * across the codebase.
 *
 * This module is the new single source of truth. ForecastItaly +
 * MarketDashboard + the worker will progressively read from here in
 * later 7.5 sub-steps. This first sub-step just introduces the type
 * and the Italy entry; nothing in the dashboard's rendering pipeline
 * uses these values yet (the existing hardcodes stay until 7.5b
 * threads them through).
 *
 * Add a new market by appending a config object below — no other
 * code change needed once the genericization is complete.
 */

export interface MarketConfig {
  /** URL slug. Must match the worker's marketIds + HubSpot enum slug. */
  slug: string;
  /** Display label (matches HubSpot country_of_origin label). */
  label: string;
  /** Flag emoji, shown in the dashboard header. */
  flag: string;
  /** Currency code for money formatting (Intl.NumberFormat). */
  currency: "EUR" | "USD" | "GBP";
  /** BCP 47 locale used by Intl number/date formatters. */
  locale: string;
  /** ISO timestamp — fiscal year start (inclusive). */
  fiscalYearStart: string;
  /** ISO timestamp — fiscal year end (inclusive). */
  fiscalYearEnd: string;
  /**
   * Exact value the worker matches on HubSpot's `country_of_origin`
   * property. Usually the same as `label` but kept separate in case
   * HubSpot's enum value differs from its display label.
   */
  hubspotCountryFilter: string;
  /** Per-AE monthly target in the market's currency, before sales split. */
  monthlyTargetPerAE: number;
  /**
   * Share of total revenue attributable to sales-driven deals.
   * For Italy: 0.65 → €7k × 0.65 = €4.55k effective monthly target.
   */
  salesSplit: number;
  /**
   * Long-form name shown on the welcome / picker UI. Optional —
   * falls back to `label` if absent.
   */
  longName?: string;
}

/**
 * Registry of all configured markets, keyed by slug. The order matters
 * for the picker UX (Phase 7.4 cards iterate user.marketIds, not this
 * map, so order here is purely aesthetic for any future "all markets"
 * view).
 */
export const MARKETS: Record<string, MarketConfig> = {
  italy: {
    slug: "italy",
    label: "Italy",
    longName: "Italy GTM",
    flag: "🇮🇹",
    currency: "EUR",
    locale: "it-IT",
    fiscalYearStart: "2026-02-01T00:00:00Z",
    fiscalYearEnd:   "2026-12-31T23:59:59Z",
    hubspotCountryFilter: "Italy",
    monthlyTargetPerAE: 7000,
    salesSplit: 0.65,
  },
};

/** Returns the config for a slug, or null if the market isn't registered. */
export function getMarketConfig(slug: string | undefined | null): MarketConfig | null {
  if (!slug) return null;
  return MARKETS[slug.toLowerCase()] || null;
}

/** All registered market slugs. Useful for fallbacks / catalog merging. */
export function listMarketSlugs(): string[] {
  return Object.keys(MARKETS);
}

/**
 * Convenience: is this market actually wired up in the dashboard? Today
 * this is identical to "registered in MARKETS", but it leaves room for
 * a flag like `wiredUp: false` in the future so a market can sit in
 * the registry as a placeholder (e.g. announced but not yet live).
 */
export function isMarketSupported(slug: string | undefined | null): boolean {
  return !!getMarketConfig(slug);
}
